import React, { useState } from 'react';
import PropTypes from 'prop-types';
import FundingInstructionsCollapsibleCard
  from 'Components/Modals/AccountFundingInstructions/FundingInstructionsCollapsibleCard/FundingInstructionsCollapsibleCard';
import DataContainer from 'Components/UI/DataContainer';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import fr from 'facet-rest';
import moment from 'moment';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { Skeleton } from '@material-ui/lab';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box/Box';

const useStyles = buildUseStyles('FundingInstructionsCollapsibleCardSkeleton', (theme) => ({
  root: {
    width: '100%',
  },
  skeleton: {
    borderRadius: theme.radius.md,
  },
  loading: {
    position: 'relative',
    top: '-39px', // This is the (skeletonWidth + Spinner)/2 to get it centered -> (58 + 20) / 2 = 39
    padding: 0,
  },
}));

const FundingInstructionsCollapsibleCardSkeleton = () => {
  const theme = useTheme();
  const classes = useStyles(theme);

  return (
    <Box className={classes.root}>
      <Skeleton
        className={classes.skeleton}
        variant="rect"
        height={58}
      />
      <LoadingIndicator className={classes.loading} size={20} />
    </Box>
  );
};

const FundingInstructionsCollapsibleCardContainer = ({
  className,
  title,
  subtitle,
  fundingInstructionsLink,
  action,
  actionLabel,
  defaultExpanded,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState(null);
  const [fundingInstructions, setFundingInstructions] = useState(null);
  const unformattedDate = fundingInstructions?.attr('date') || fundingInstructions?.attr('startDate');
  const date = unformattedDate ? moment(unformattedDate).utc().format('L') : '';

  useComponentDidMount(async () => {
    try {
      const [fundingInstructionsResource] = await fundingInstructionsLink.get();
      setFundingInstructions(fundingInstructionsResource);
    } catch (e) {
      setErrors(e.errors);
    } finally {
      setLoading(false);
    }
  });

  return (
    <DataContainer
      className={classes.root}
      loading={loading}
      loadingError={errors}
      component={FundingInstructionsCollapsibleCard}
      skeleton={FundingInstructionsCollapsibleCardSkeleton}
      componentProps={{
        className,
        title: title || fundingInstructions?.attr('title'),
        subtitle,
        amount: fundingInstructions?.attr('amount'),
        date,
        additionalInstructions: fundingInstructions?.attr('additionalInstructions'),
        action,
        actionLabel,
        defaultExpanded,
        test: 'ach-relationship-funding-instructions',
      }}
    />
  );
};

FundingInstructionsCollapsibleCardContainer.defaultProps = {
  className: '',
  title: '',
  subtitle: '',
  action: undefined,
  actionLabel: 'Get Started',
  defaultExpanded: false,
};

FundingInstructionsCollapsibleCardContainer.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  fundingInstructionsLink: PropTypes.instanceOf(fr.Link).isRequired,
  action: PropTypes.func,
  actionLabel: PropTypes.string,
  defaultExpanded: PropTypes.bool,
};

export default FundingInstructionsCollapsibleCardContainer;
